import type { ComponentType } from 'react'

import { PostFields } from '@buffer-mono/reminders-config'

import type { PostTypeBarOption } from '~publish/components/Composer/components/PostTypeBar'
import type { StickerChipOption } from '~publish/components/Composer/components/RemindersWithStickers/StickerChipsField'
import { ShopGridLinkLabel } from '~publish/components/Composer/components/ShopGridLinkLabel'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import {
  PostTypeEvent,
  PostTypeFacebookReel,
  PostTypeFacebookStory,
  PostTypeGhostPost,
  PostTypeOffer,
  PostTypePost,
  PostTypeReel,
  PostTypeShort,
  PostTypeStory,
  PostTypeThread,
  PostTypeWhatsNew,
} from '~publish/legacy/post/constants'

export type LockedField =
  | Readonly<{
      kind: 'text'
      label: string
      placeholder: string
      Label?: ComponentType
    }>
  | Readonly<{
      kind: 'chips'
      label: string
      options: readonly StickerChipOption[]
    }>

type GuestNetworkSettings = Readonly<{
  postTypes?: readonly PostTypeBarOption[]
  fields?: readonly LockedField[]
}>

const text = (
  label: string,
  placeholder: string,
): Extract<LockedField, { kind: 'text' }> => ({
  kind: 'text',
  label,
  placeholder,
})

const FIRST_COMMENT = text('First Comment', 'Your comment')

const GUEST_NETWORK_SETTINGS: Partial<
  Record<GuestNetworkId, GuestNetworkSettings>
> = {
  instagram: {
    postTypes: [
      { value: PostTypePost, name: 'Post' },
      { value: PostTypeReel, name: 'Reel' },
      { value: PostTypeStory, name: 'Story' },
    ],
    fields: [
      {
        kind: 'chips',
        label: 'Add Stickers',
        options: [
          { field: PostFields.MUSIC, label: 'Music' },
          { field: PostFields.PRODUCTS, label: 'Tag Products' },
        ],
      },
      FIRST_COMMENT,
      {
        ...text('Shop Grid Link', 'Website or Product URL'),
        Label: ShopGridLinkLabel,
      },
    ],
  },
  linkedin: {
    fields: [FIRST_COMMENT],
  },
  'facebook-page': {
    postTypes: [
      { value: PostTypePost, name: 'Post' },
      { value: PostTypeFacebookReel, name: 'Reel' },
      { value: PostTypeFacebookStory, name: 'Story' },
    ],
  },
  tiktok: {
    fields: [text('Title', 'Your post title')],
  },
  threads: {
    postTypes: [
      { value: PostTypeThread, name: 'Thread' },
      { value: PostTypeGhostPost, name: 'Ghost Post' },
    ],
    fields: [
      text('Topic', 'Type the topic'),
      text('Location', 'Type the location'),
    ],
  },
  youtube: {
    postTypes: [{ value: PostTypeShort, name: 'Short' }],
  },
  pinterest: {
    fields: [
      text('Title', 'Your pin title'),
      text('Destination Link', 'Enter destination link...'),
      text('Pinning To', 'Select boards'),
    ],
  },
  mastodon: {
    fields: [text('Content Warning', 'Add a warning message to your post')],
  },
  googlebusiness: {
    postTypes: [
      { value: PostTypeWhatsNew, name: "What's New" },
      { value: PostTypeOffer, name: 'Offer' },
      { value: PostTypeEvent, name: 'Event' },
    ],
    fields: [text('Button', 'None')],
  },
}

export function getGuestNetworkSettings(
  network: GuestNetworkId,
): GuestNetworkSettings | undefined {
  return GUEST_NETWORK_SETTINGS[network]
}
